import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import HeaderStyleThree from '../../components/Layout/Header/HeaderStyleThree';
import Footer from '../../components/Layout/Footer';
import BackToTop from '../../components/elements/BackToTop';
import fallbackTrainingsData from '../../data/trainings.json';

const LEVEL_COLORS = {
    Beginner: '#69ff47',
    Intermediate: '#00e5ff',
    Advanced: '#FFD700',
    'Beginner – Intermediate': '#69ff47',
};

export default function TrainingDetail() {
    const { trainingId } = useParams();
    
    const [training, setTraining] = useState(null);
    const [pageContent, setPageContent] = useState(fallbackTrainingsData.page);
    const [loading, setLoading] = useState(true);
    
    const [isVisible, setIsVisible] = useState(false);
    const [activeDay, setActiveDay] = useState(null);

    useEffect(() => {
        const h = () => setIsVisible(window.scrollY > 300);
        document.addEventListener('scroll', h);
        return () => document.removeEventListener('scroll', h);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch page content
                const { data: pData } = await supabase.from('trainings_page_content').select('data').eq('page_name', 'trainings-page').single();
                if (pData) setPageContent({ ...fallbackTrainingsData.page, ...pData.data });

                // Fetch specific training
                const { data: tData } = await supabase.from('trainings').select('data').eq('slug', trainingId).single();
                if (tData) {
                    setTraining({ id: trainingId, ...tData.data });
                } else {
                    // Fallback to JSON if not in DB
                    const fallback = fallbackTrainingsData.trainings.find(t => t.id === trainingId);
                    if (fallback) setTraining(fallback);
                }
            } catch (err) {
                console.error("Error fetching training details:", err);
                const fallback = fallbackTrainingsData.trainings.find(t => t.id === trainingId);
                if (fallback) setTraining(fallback);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [trainingId]);

    useEffect(() => {
        if (training && training.outline) {
            setActiveDay(Object.keys(training.outline)[0]);
        }
    }, [training]);

    if (loading) return null; // Avoid flashing the not-found state

    if (!training) {
        const dp = pageContent.detailPage || fallbackTrainingsData.page.detailPage;
        return (
            <div className="homestyle2">
                <HeaderStyleThree />
                <section style={{ padding: '200px 0 100px', textAlign: 'center', background: 'var(--eventflow-black)' }}>
                    <div className="container">
                        <h2 style={{ color: '#fff', fontSize: '36px', marginBottom: '20px' }}>{dp.notFoundTitle}</h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '30px' }}>{dp.notFoundText}</p>
                        <Link to="/trainings" className="thm-btn">{dp.notFoundButtonText}</Link>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    const levelColor = LEVEL_COLORS[training.level] || '#00e5ff';
    const outlineDays = training.outline ? Object.keys(training.outline) : [];
    const dp = pageContent.detailPage || fallbackTrainingsData.page.detailPage;

    return (
        <div className="homestyle2">
            <HeaderStyleThree />
            <style>{`
                /* ── Hero ── */
                .td-hero {
                    position: relative;
                    padding: 160px 0 60px;
                    background: var(--eventflow-black);
                    overflow: hidden;
                }
                .td-hero::before {
                    content: '';
                    position: absolute; inset: 0;
                    background:
                        radial-gradient(ellipse at 50% 0%, rgba(var(--eventflow-primary-rgb),0.15) 0%, transparent 60%),
                        radial-gradient(ellipse at 80% 90%, rgba(var(--eventflow-base-rgb),0.08) 0%, transparent 50%);
                    pointer-events: none;
                }
                .td-hero::after {
                    content: '';
                    position: absolute; inset: 0;
                    background-image:
                        linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
                    background-size: 60px 60px;
                    pointer-events: none;
                }
                .td-hero__inner { position: relative; z-index: 2; }
                .td-hero__eyebrow {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-family: var(--eventflow-font);
                    font-size: 12px; font-weight: 700;
                    letter-spacing: 3px; text-transform: uppercase;
                    color: var(--eventflow-base); margin-bottom: 20px;
                }
                .td-hero__eyebrow::before, .td-hero__eyebrow::after {
                    content: ''; display: inline-block;
                    width: 28px; height: 1px;
                    background: var(--eventflow-base); opacity: 0.5;
                }
                .td-hero__title {
                    font-family: var(--eventflow-font);
                    font-size: 48px; font-weight: 900;
                    color: #fff; line-height: 1.1;
                    letter-spacing: -1.5px; margin-bottom: 16px;
                    max-width: 800px;
                }
                .td-hero__tagline {
                    font-family: var(--eventflow-font);
                    font-size: 16px; color: rgba(255,255,255,0.5);
                    max-width: 650px; line-height: 1.7; margin-bottom: 28px;
                }
                .td-hero__breadcrumb {
                    font-family: var(--eventflow-font);
                    font-size: 13px; color: rgba(255,255,255,0.35);
                    margin-bottom: 24px;
                }
                .td-hero__breadcrumb a {
                    color: var(--eventflow-base);
                    text-decoration: none;
                }
                .td-hero__breadcrumb a:hover { text-decoration: underline; }

                /* Meta pills */
                .td-hero__meta {
                    display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 32px;
                }
                .td-hero__meta-item {
                    display: inline-flex; align-items: center; gap: 8px;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 50px; padding: 9px 18px;
                    font-family: var(--eventflow-font);
                    font-size: 13px; color: rgba(255,255,255,0.6);
                }
                .td-hero__meta-item i { color: var(--eventflow-base); font-size: 12px; }

                /* Hero actions */
                .td-hero__actions {
                    display: flex; gap: 14px; flex-wrap: wrap;
                }
                .td-hero__back {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-family: var(--eventflow-font);
                    font-size: 13px; font-weight: 600;
                    color: rgba(255,255,255,0.5);
                    text-decoration: none;
                    padding: 12px 22px;
                    border-radius: 50px;
                    border: 1px solid rgba(255,255,255,0.1);
                    transition: all 0.3s;
                }
                .td-hero__back:hover {
                    color: #fff;
                    border-color: rgba(255,255,255,0.25);
                }

                /* ── Content Section ── */
                .td-content {
                    background-color: rgba(4, 0, 10, 0.70);
                    padding: 80px 0 100px;
                    position: relative;
                }
                .td-content__grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 60px;
                }

                /* Section blocks */
                .td-block {
                    position: relative;
                }
                .td-block__title {
                    font-family: var(--eventflow-font);
                    font-size: 24px; font-weight: 800;
                    color: #fff; margin-bottom: 20px;
                    padding-bottom: 14px;
                    border-bottom: 1px solid rgba(255,255,255,0.06);
                }
                .td-block__text {
                    font-family: var(--eventflow-font);
                    font-size: 15px; color: rgba(255,255,255,0.55);
                    line-height: 1.8; margin-bottom: 16px;
                }
                .td-block__text:last-child { margin-bottom: 0; }

                /* Outline tabs */
                .td-outline-tabs {
                    display: flex; gap: 10px; margin-bottom: 24px; flex-wrap: wrap;
                }
                .td-outline-tab {
                    font-family: var(--eventflow-font);
                    font-size: 13px; font-weight: 700;
                    padding: 10px 22px;
                    border-radius: 50px;
                    border: 1px solid rgba(255,255,255,0.1);
                    background: transparent;
                    color: rgba(255,255,255,0.5);
                    cursor: pointer;
                    transition: all 0.3s;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .td-outline-tab:hover {
                    color: #fff;
                    border-color: rgba(255,255,255,0.25);
                }
                .td-outline-tab--active {
                    background: var(--eventflow-base);
                    border-color: var(--eventflow-base);
                    color: #fff;
                }

                /* Outline list */
                .td-outline-list {
                    list-style: none; padding: 0; margin: 0;
                }
                .td-outline-list li {
                    display: flex; align-items: flex-start; gap: 12px;
                    padding: 14px 0;
                    border-bottom: 1px solid rgba(255,255,255,0.04);
                    font-family: var(--eventflow-font);
                    font-size: 14px; color: rgba(255,255,255,0.6);
                    line-height: 1.6;
                }
                .td-outline-list li:last-child { border-bottom: none; }
                .td-outline-list li::before {
                    content: '›';
                    color: var(--eventflow-base);
                    font-size: 18px; font-weight: 700;
                    flex-shrink: 0;
                    line-height: 1.4;
                }

                /* Bullet lists (who should attend, requirements) */
                .td-bullet-list {
                    list-style: none; padding: 0; margin: 0;
                }
                .td-bullet-list li {
                    display: flex; align-items: flex-start; gap: 12px;
                    padding: 10px 0;
                    font-family: var(--eventflow-font);
                    font-size: 14px; color: rgba(255,255,255,0.55);
                    line-height: 1.6;
                }
                .td-bullet-list li i {
                    color: var(--eventflow-base);
                    font-size: 10px;
                    margin-top: 6px;
                    flex-shrink: 0;
                }
                .td-bullet-list--warn li i {
                    color: #ff6b6b;
                }

                /* Trainers */
                .td-trainers {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 24px;
                }
                .td-trainer-card {
                    background: rgba(255,255,255,0.03);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 16px;
                    padding: 28px;
                    transition: all 0.3s;
                }
                .td-trainer-card:hover {
                    border-color: rgba(255,255,255,0.15);
                    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
                }
                .td-trainer-card__img {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    background: #ffffff;
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 16px;
                    overflow: hidden;
                    position: relative;
                }
                .td-trainer-card__img img {
                    width: 100%; height: 100%; object-fit: cover;
                }
                .td-trainer-card__img span {
                    font-family: var(--eventflow-font);
                    font-size: 11px; font-weight: 700;
                    color: #333;
                }
                .td-trainer-card__name {
                    font-family: var(--eventflow-font);
                    font-size: 18px; font-weight: 800;
                    color: #fff; margin-bottom: 6px;
                }
                .td-trainer-card__title {
                    font-family: var(--eventflow-font);
                    font-size: 11px; font-weight: 700;
                    letter-spacing: 1px; text-transform: uppercase;
                    color: var(--eventflow-base);
                    margin-bottom: 14px;
                }
                .td-trainer-card__bio {
                    font-family: var(--eventflow-font);
                    font-size: 13px; color: rgba(255,255,255,0.45);
                    line-height: 1.7; margin-bottom: 16px;
                }
                .td-trainer-card__socials {
                    display: flex; gap: 10px;
                }
                .td-trainer-card__social {
                    width: 32px; height: 32px;
                    border-radius: 8px;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid rgba(255,255,255,0.08);
                    display: flex; align-items: center; justify-content: center;
                    color: rgba(255,255,255,0.5);
                    font-size: 13px;
                    text-decoration: none;
                    transition: all 0.2s;
                }
                .td-trainer-card__social:hover {
                    background: rgba(var(--eventflow-base-rgb),0.15);
                    border-color: rgba(var(--eventflow-base-rgb),0.3);
                    color: var(--eventflow-base);
                }

                /* Register CTA */
                .td-register-cta {
                    margin-top: 60px;
                    background: rgba(var(--eventflow-base-rgb),0.05);
                    border: 1px solid rgba(var(--eventflow-base-rgb),0.2);
                    border-radius: 16px;
                    padding: 36px 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 16px;
                    flex-wrap: wrap;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .td-hero { padding: 130px 0 50px; }
                    .td-hero__title { font-size: 32px; letter-spacing: -0.5px; }
                    .td-register-cta {
                        flex-direction: column;
                        padding: 24px 20px;
                    }
                    .td-trainers {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>

            {/* Hero */}
            <section className="td-hero">
                <div className="container">
                    <div className="td-hero__inner wow fadeInUp" data-wow-delay="100ms">
                        <div className="td-hero__breadcrumb">
                            <Link to="/trainings">Trainings</Link> / {training.title}
                        </div>
                        <div className="td-hero__eyebrow">{dp.heroEyebrow}</div>
                        <h1 className="td-hero__title">{training.title}</h1>
                        <p className="td-hero__tagline">{training.tagline}</p>

                        <div className="td-hero__meta">
                            <span className="td-hero__meta-item">
                                <i className="fa fa-calendar-alt" />{pageContent.trainingDates}
                            </span>
                            <span className="td-hero__meta-item">
                                <i className="fa fa-clock" />{training.duration}
                            </span>
                            <span className="td-hero__meta-item">
                                <i className="fa fa-chair" />{training.seats}
                            </span>
                            <span className="td-hero__meta-item" style={{ color: levelColor, borderColor: `${levelColor}30` }}>
                                <i className="fa fa-signal" style={{ color: levelColor }} />{training.level}
                            </span>
                            <span className="td-hero__meta-item">
                                <i className="fa fa-user" />{training.instructor}
                            </span>
                        </div>

                        <div className="td-hero__actions">
                            {training.registerLink ? (
                                <a href={training.registerLink} className="thm-btn" target="_blank" rel="noopener noreferrer">
                                    {dp.registerButtonText} <span className="icon-arrow-right" />
                                </a>
                            ) : (
                                <span className="thm-btn" style={{ opacity: 0.6, cursor: 'default' }}>
                                    {dp.registerComingSoonText}
                                </span>
                            )}
                            <Link to="/trainings" className="td-hero__back">
                                <i className="fa fa-arrow-left" /> {dp.backButtonText}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="td-content">
                <div className="container">
                    <div className="td-content__grid">

                        {/* Abstract */}
                        {training.abstract && training.abstract.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="100ms">
                                <h3 className="td-block__title">{dp.abstractTitle}</h3>
                                {training.abstract.map((para, i) => (
                                    <p key={i} className="td-block__text">{para}</p>
                                ))}
                            </div>
                        )}

                        {/* Outline */}
                        {training.outline && outlineDays.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="150ms">
                                <h3 className="td-block__title">{dp.outlineTitle}</h3>
                                {outlineDays.length > 1 && (
                                    <div className="td-outline-tabs">
                                        {outlineDays.map(day => (
                                            <button
                                                key={day}
                                                className={`td-outline-tab${activeDay === day ? ' td-outline-tab--active' : ''}`}
                                                onClick={() => setActiveDay(day)}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <ul className="td-outline-list">
                                    {(training.outline[activeDay] || []).map((item, i) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Who Should Attend */}
                        {training.whoShouldAttend && training.whoShouldAttend.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="200ms">
                                <h3 className="td-block__title">{dp.whoShouldAttendTitle}</h3>
                                <ul className="td-bullet-list">
                                    {training.whoShouldAttend.map((item, i) => (
                                        <li key={i}><i className="fa fa-check-circle" />{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Who Should Not */}
                        {training.whoShouldNot && training.whoShouldNot.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="220ms">
                                <h3 className="td-block__title">{dp.whoShouldNotTitle}</h3>
                                <ul className="td-bullet-list td-bullet-list--warn">
                                    {training.whoShouldNot.map((item, i) => (
                                        <li key={i}><i className="fa fa-times-circle" />{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Audience Level */}
                        {training.audienceLevel && training.audienceLevel.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="240ms">
                                <h3 className="td-block__title">{dp.audienceLevelTitle}</h3>
                                <ul className="td-bullet-list">
                                    {training.audienceLevel.map((item, i) => (
                                        <li key={i}><i className="fa fa-circle" />{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Requirements */}
                        {training.requirements && training.requirements.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="260ms">
                                <h3 className="td-block__title">{dp.requirementsTitle}</h3>
                                <ul className="td-bullet-list">
                                    {training.requirements.map((item, i) => (
                                        <li key={i}><i className="fa fa-laptop" />{item}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Trainers */}
                        {training.trainers && training.trainers.length > 0 && (
                            <div className="td-block wow fadeInUp" data-wow-delay="280ms">
                                <h3 className="td-block__title">{dp.trainersTitle}</h3>
                                <div className="td-trainers">
                                    {training.trainers.map((trainer, i) => (
                                        <div key={i} className="td-trainer-card">
                                            <div className="td-trainer-card__img">
                                                {trainer.img && trainer.img.startsWith('http') ? (
                                                    <img src={trainer.img} alt={trainer.name} />
                                                ) : (
                                                    <span>{dp.imagePlaceholderText}</span>
                                                )}
                                            </div>
                                            <div className="td-trainer-card__name">{trainer.name}</div>
                                            <div className="td-trainer-card__title">{trainer.title}</div>
                                            <p className="td-trainer-card__bio">{trainer.bio}</p>
                                            <div className="td-trainer-card__socials">
                                                {trainer.twitter && (
                                                    <a href={trainer.twitter} className="td-trainer-card__social" target="_blank" rel="noopener noreferrer">
                                                        <i className="fab fa-twitter" />
                                                    </a>
                                                )}
                                                {trainer.linkedin && (
                                                    <a href={trainer.linkedin} className="td-trainer-card__social" target="_blank" rel="noopener noreferrer">
                                                        <i className="fab fa-linkedin" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Register CTA */}
                    <div className="td-register-cta wow fadeInUp" data-wow-delay="300ms">
                        {training.registerLink ? (
                            <a href={training.registerLink} className="thm-btn" target="_blank" rel="noopener noreferrer">
                                {dp.registerButtonText} <span className="icon-arrow-right" />
                            </a>
                        ) : (
                            <span className="thm-btn" style={{ opacity: 0.6, cursor: 'default' }}>
                                {dp.registerComingSoonText}
                            </span>
                        )}
                    </div>
                </div>
            </section>

            <BackToTop scroll={isVisible} />
            <Footer />
        </div>
    );
}
